import React, { useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { decode, isBlurhashValid } from 'blurhash';
import { computeDisplaySize } from './panda-view-geometry';

const MAX_DIMENSION = 32; // max pixels per side

/**
 * Computes the decode resolution from photo dimensions:
 * - max 32px on the longest side
 * - aspect-ratio preserved from rawDimensions
 * - falls back to 32×32 when dimensions are unavailable
 */
const computeDecodeSize = (rawDimensions) => {
  if (rawDimensions && rawDimensions.w > 0 && rawDimensions.h > 0) {
    const { w, h } = rawDimensions;

    if (w >= h) {
      // landscape/square
      return {
        width: MAX_DIMENSION,
        height: Math.max(1, Math.round((MAX_DIMENSION * h) / w)),
      };
    }

    // portrait
    return {
      width: Math.max(1, Math.round((MAX_DIMENSION * w) / h)),
      height: MAX_DIMENSION,
    };
  }

  // fallback
  return { width: MAX_DIMENSION, height: MAX_DIMENSION };
};

/**
 * Renders a BlurHash as a `<canvas>` element.
 *
 *  Decoding happens at a small resolution (≤32px) for performance
 *  in offscreen canvas scales the result up via drawImage.
 *
 * @param {string|null}    hash          - BlurHash string
 * @param {{w,h}|null}     rawDimensions - Original image dimensions (for aspect ratio)
 * @param {number[]|null}  sizeArea      - Print size dimensions as [width, height] (e.g. [6, 4]
 *                                         for a 6×4 print). Used to compute the canvas display
 *                                         size via computeDisplaySize so it matches PandaView's
 *                                         canvas exactly. Falls back to the photo's own aspect
 *                                         ratio when absent.
 * @param {string|null}    fx            - 'greyscale' | 'sepia' | 'colour' | null
 * @param {boolean}        fill          - When true, canvas fills its container via
 *                                         100% width/height (used for overlay mode).
 */
const BlurhashCanvas = ({ hash, rawDimensions, sizeArea, fx, fill }) => {
  const canvasRef = useRef(null);

  const { width: displayWidth, height: displayHeight } = useMemo(
    () => computeDisplaySize(rawDimensions, sizeArea),
    [rawDimensions, sizeArea],
  );

  const { width: decodeWidth, height: decodeHeight } = useMemo(
    () => computeDecodeSize(rawDimensions),
    [rawDimensions],
  );

  useEffect(() => {
    if (!hash) return;
    const { result } = isBlurhashValid(hash);
    if (!result) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const pixels = decode(hash, decodeWidth, decodeHeight);

    // Render decoded pixels onto a tiny offscreen canvas
    const offscreen = document.createElement('canvas');
    offscreen.width = decodeWidth;
    offscreen.height = decodeHeight;
    const offCtx = offscreen.getContext('2d');
    if (!offCtx) return;
    const imageData = offCtx.createImageData(decodeWidth, decodeHeight);
    imageData.data.set(pixels);
    offCtx.putImageData(imageData, 0, 0);

    // Scale up to fill the display canvas (same approach as PandaView)
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(offscreen, 0, 0, displayWidth, displayHeight);
  }, [hash, decodeWidth, decodeHeight, displayWidth, displayHeight]);

  // If applicable, style placeholder with filter to mimick actual thumbnail
  const cssFilter =
    fx === 'greyscale'
      ? 'grayscale(100%)'
      : fx === 'sepia'
        ? 'sepia(100%)'
        : undefined;

  // In overlay (fill) mode: stretch to cover the absolutely-positioned container.
  // In normal (placeholder) mode: let the CSS mixin handle sizing as it does for PandaView.
  const style = fill
    ? { width: '100%', height: '100%', display: 'block', filter: cssFilter }
    : cssFilter
      ? { filter: cssFilter }
      : undefined;

  return (
    <canvas
      ref={canvasRef}
      width={displayWidth}
      height={displayHeight}
      className={fill ? undefined : 'img-fluid'}
      style={style}
    />
  );
};

BlurhashCanvas.propTypes = {
  hash: PropTypes.string,
  rawDimensions: PropTypes.shape({
    w: PropTypes.number,
    h: PropTypes.number,
  }),
  sizeArea: PropTypes.arrayOf(PropTypes.number),
  fx: PropTypes.string,
  fill: PropTypes.bool,
};

BlurhashCanvas.defaultProps = {
  hash: null,
  rawDimensions: null,
  sizeArea: null,
  fx: null,
  fill: false,
};

export default BlurhashCanvas;
